import { useEffect, useState } from "react"
import { IEventos } from "../../interfaces/IEventos"
import { TipoEvento } from "../../interfaces/TipoEvento"
import { Action, Column } from "../../interfaces/CamposTablaGenerica"
import { Button, Col, Container, Modal, Row } from "react-bootstrap"
import AddEventoModal from "./AddEventoModal"
import EditEventoModal from "./EditEventoModal"
import SkeletonCard from "../../components/SkeletonCard/SkeletonCard"
import EventosPage from "../EventosPage/EventosPage"
import { sortEventosByDate } from "../../utils/dateUtils"
import { useSpinner } from "../../contexts/SpinnerProvider"
import { useApi } from "../../hooks/useApi"
import { API_ENDPOINTS } from "../../api/endpoints"

const Eventos = () => {
  const [eventos, setEventos] = useState<IEventos[]>([])
  const { addLoading, removeLoading } = useSpinner()
  const [skeletonLoading, setSkeletonLoading] = useState(false)
  const [addModalShow, setAddModalShow] = useState(false)
  const [fechaDefectoModal, setFechaDefectoModal] = useState<
    string | undefined
  >(undefined)
  const [tipoDefectoModal, setTipoDefectoModal] = useState<
    TipoEvento | undefined
  >(undefined)
  const [editModalShow, setEditModalShow] = useState(false)
  const [selectedEvento, setSelectedEvento] = useState<IEventos | null>(null)
  const API_URL = API_ENDPOINTS.EVENTOS
  const [deleteConfirmation, setDeleteConfirmation] = useState(false)
  const [selectedToDelete, setSelectedToDelete] = useState<IEventos | null>(
    null,
  )
  const api = useApi()

  const callApiAndSetEventos = async () => {
    setSkeletonLoading(true)
    try {
      const responseData = await api.get<IEventos[]>(API_URL, {
        errorMessage: "Error al cargar los eventos",
      })
      setEventos(sortEventosByDate(responseData))
    } catch {
      // toast ya mostrado por useApi
    } finally {
      setSkeletonLoading(false)
    }
  }

  useEffect(() => {
    callApiAndSetEventos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_URL])

  const actions: Action = {
    create: true,
    update: true,
    delete: true,
  }

  const columns: Column<IEventos>[] = [
    { title: "Nombre", field: "nombre" },
    { title: "Fecha", field: "fecha" },
  ]

  const handleEventoAdd = async (evento: IEventos) => {
    addLoading()
    try {
      const newEvento = await api.post<IEventos>(API_URL, evento, {
        errorMessage: "Error al agregar el evento",
      })
      if (newEvento) setEventos([...eventos, newEvento])
      callApiAndSetEventos()
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  const handleEventoEdit = async (evento: IEventos) => {
    addLoading()
    try {
      const updatedEvento = await api.put<IEventos>(
        `${API_URL}/${evento.id}`,
        evento,
        { errorMessage: "Error al editar el evento" },
      )
      if (updatedEvento) {
        setEventos(
          eventos.map((item) =>
            item.id === evento.id ? updatedEvento : item,
          ),
        )
        callApiAndSetEventos()
      }
    } catch {
      // toast ya mostrado
    } finally {
      removeLoading()
    }
  }

  const showDeleteConfirmation = (item: IEventos) => {
    setSelectedToDelete(item)
    setDeleteConfirmation(true)
  }

  const handleDeleteConfirmation = async (confirmed: boolean) => {
    if (confirmed && selectedToDelete) {
      const eventoId: number = selectedToDelete.id
      addLoading()
      try {
        await api.del(`${API_URL}/${eventoId}`, {
          errorMessage: "Error al eliminar el evento",
        })
        setEventos(eventos.filter((item) => item.id !== eventoId))
      } catch {
        // toast ya mostrado
      } finally {
        removeLoading()
      }
    }
    setSelectedToDelete(null)
    setDeleteConfirmation(false)
  }

  const handleAddModalOpen = (fechaDefecto?: string, tipoDefecto?: string) => {
    setFechaDefectoModal(fechaDefecto)
    setTipoDefectoModal(tipoDefecto as TipoEvento | undefined)
    setAddModalShow(true)
  }
  const handleAddModalClose = () => {
    setAddModalShow(false)
    setFechaDefectoModal(undefined)
    setTipoDefectoModal(undefined)
  }

  const handleEditModalOpen = (item: IEventos) => {
    setSelectedEvento(item)
    setEditModalShow(true)
  }
  const handleEditModalClose = () => {
    setEditModalShow(false)
    setSelectedEvento(null)
  }

  return (
    <Container fluid>
      <h1 className="text-center mt-3 mb-3" style={{ fontSize: "2.25rem" }}>
        Eventos
      </h1>
      {skeletonLoading ? (
        <SkeletonCard />
      ) : (
        <>
          <Row className="mt-1">
            <Col sm={12}>
              <EventosPage
                data={eventos}
                columns={columns}
                actions={actions}
                onAdd={handleAddModalOpen}
                onUpdate={handleEditModalOpen}
                onDelete={showDeleteConfirmation}
              />

              {deleteConfirmation && (
                <Modal
                  show={deleteConfirmation}
                  onHide={() => setDeleteConfirmation(false)}
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Confirmar eliminación</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <p>⚠️ Atención!</p>
                    <p>
                      Si borra este evento tambien se borraran los egresados
                      relacionados <strong>de forma permanente.</strong>
                    </p>
                    <p>¿Estás seguro de que deseas eliminar este evento?</p>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="secondary"
                      onClick={() => handleDeleteConfirmation(false)}
                    >
                      No
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => handleDeleteConfirmation(true)}
                    >
                      Sí
                    </Button>
                  </Modal.Footer>
                </Modal>
              )}

              <AddEventoModal
                show={addModalShow}
                handleClose={handleAddModalClose}
                handleEventoAdd={handleEventoAdd}
                eventos={eventos}
                fechaDefecto={fechaDefectoModal}
                tipoDefecto={tipoDefectoModal}
              />

              <EditEventoModal
                show={editModalShow}
                handleClose={handleEditModalClose}
                handleEventoEdit={handleEventoEdit}
                selectedEvento={selectedEvento}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}

export default Eventos
